import './LegacyBooks.css'

type LegacyBook = {
  id: string
  title: string
  subtitle: string
  description: string
  cover: string
  points?: string[]
  quote?: string
  featured?: boolean
}

const legacyBooks: LegacyBook[] = [
  {
    id: 'my-legacy',
    title: 'ECHO My Legacy Journal',
    subtitle: 'Your life. Your story. Your legacy.',
    description:
      'A guided journal to reflect, record, and organize the people, moments, values, and important information that make up your life story — so the people you love have clarity for generations to come.',
    cover: '/books/echo-my-legacy.jpg',
    points: [
      'Reflect on the people, moments, and values that shaped you.',
      'Plan the wishes and information your family should never have to hunt for.',
      'Preserve your voice, so love and wisdom are not left to memory alone.',
    ],
    quote: '"For everything there is a season, and a time for every purpose under heaven." — Ecclesiastes 3:1',
    featured: true,
  },
  {
    id: 'our-legacy',
    title: 'ECHO Our Legacy Journal',
    subtitle: 'A story you keep together',
    description:
      'A shared journal for the life you are building side by side — a place to record your people, your values, and the wishes you want held in common.',
    cover: '/books/echo-our-legacy.jpg',
  },
  {
    id: 'business-owners',
    title: 'ECHO Business Owners',
    subtitle: 'Companion Journal',
    description:
      'Your business is more than a business. It is a legacy. This companion helps you preserve your vision, document what you have built, and prepare the people who will carry it forward.',
    cover: '/books/echo-business-owners.jpg',
    points: [
      'Clarify your purpose and long-term vision.',
      'Document your knowledge and processes.',
      'Prepare for a transition, a sale, or the next generation.',
    ],
  },
  {
    id: 'pastors',
    title: 'ECHO for Pastors',
    subtitle: 'Ministry Legacy Companion',
    description:
      'A companion for pastors and ministry leaders — to organize calling, church relationships, pastoral wishes, and the unfinished work, so the ministry can continue with clarity.',
    cover: '/books/echo-pastors.jpg',
    quote: '"Well done, good and faithful servant." — Matthew 25:23',
  },
  {
    id: 'what-you-need',
    title: 'What You Need to Know About Me',
    subtitle: 'A Gratitude & Legacy Journal',
    description:
      'Your story matters. Take time for the moments, lessons, and people who shaped you — and leave the gratitude, memories, and wisdom you want remembered.',
    cover: '/books/echo-what-you-need.jpg',
  },
]

const LegacyBooks = () => {
  const scrollToContact = () => {
    const target = document.querySelector('#contact')
    if (target) {
      const offsetTop = (target as HTMLElement).offsetTop - 80
      window.scrollTo({ top: offsetTop, behavior: 'smooth' })
    }
  }

  const featured = legacyBooks.find((b) => b.featured)!
  const others = legacyBooks.filter((b) => !b.featured)

  return (
    <section id="legacy-books" className="collection">
      <div className="collection-intro">
        <span className="section-eyebrow">The legacy collection</span>
        <h2>Five journals. One way of saying you mattered.</h2>
        <p>
          People, values, wishes, plans, and legacy — made to be opened by the people who love you.
        </p>
      </div>

      <article className="spread">
        <figure className="spread-cover">
          <img src={featured.cover} alt={featured.title} />
        </figure>
        <div className="spread-copy">
          <p className="spread-kicker">The heart of the collection</p>
          <h3>{featured.title}</h3>
          <p className="spread-sub">{featured.subtitle}</p>
          <p>{featured.description}</p>
          {featured.points && (
            <ul className="spread-points">
              {featured.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
          {featured.quote && <blockquote>{featured.quote}</blockquote>}
          <button className="btn btn-primary" onClick={scrollToContact}>
            Begin this journal
          </button>
        </div>
      </article>

      <div className="shelf">
        {others.map((book) => (
          <article key={book.id} className="shelf-book">
            <img src={book.cover} alt="" />
            <h3>{book.title}</h3>
            <p className="shelf-sub">{book.subtitle}</p>
            <p>{book.description}</p>
          </article>
        ))}
      </div>

      <p className="collection-note">
        These journals are for reflection and organization. They are not wills, trusts, or other legal documents.
        When a decision needs legal, financial, tax, medical, or ministry guidance, consult a qualified professional.
      </p>
    </section>
  )
}

export default LegacyBooks
